import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Play, 
  Clock, 
  ChevronRight, 
  BookOpen,
  Video,
  Lock,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function FreeLessonsPage() {
  const [freeLessons, setFreeLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const fetchFreeLessons = async () => {
      try {
        const response = await axios.get(`${API}/free-lessons`);
        setFreeLessons(response.data);
        // Auto-select first video if available
        if (response.data.length > 0) {
          setSelectedVideo(response.data[0]);
        }
      } catch (err) {
        console.error("Error fetching free lessons:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFreeLessons();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="free-lessons-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 py-16 md:py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="bg-white/20 text-white border-0 mb-4">
              <Video className="w-4 h-4 mr-2" />
              Acces Gratuit
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight font-['Archivo']">
              Lecții Gratuite
            </h1>
            <p className="text-blue-100 mt-6 text-lg">
              Descoperă cum predăm legislația rutieră. Vizionează lecții gratuite 
              și convinge-te de calitatea cursurilor noastre.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : freeLessons.length === 0 ? (
            /* Empty State - Placeholder pentru când nu sunt lecții */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="max-w-2xl mx-auto">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Video className="w-12 h-12 text-blue-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 font-['Archivo']">
                  Lecțiile Gratuite Vin În Curând!
                </h2>
                <p className="text-slate-600 text-lg mb-8">
                  Pregătim lecții video gratuite pentru tine. Revino în curând pentru a vedea 
                  exemple din cursurile noastre de legislație rutieră.
                </p>
                
                {/* Preview placeholder */}
                <div className="bg-slate-900 rounded-2xl aspect-video max-w-3xl mx-auto mb-8 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
                  <div className="text-center z-10">
                    <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur">
                      <Play className="w-10 h-10 text-white ml-1" />
                    </div>
                    <p className="text-white/80 text-lg">Video în pregătire...</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="bg-blue-600 hover:bg-blue-700 font-semibold">
                      Creează Cont Gratuit
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/servicii">
                    <Button size="lg" variant="outline" className="font-semibold">
                      Vezi Categoriile
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          ) : (
            /* Content when lessons exist */
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Video Player - Main Area */}
              <div className="lg:col-span-2">
                {selectedVideo && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    key={selectedVideo.id}
                  >
                    <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl">
                      {selectedVideo.video_url ? (
                        <div className="aspect-video">
                          {selectedVideo.video_url.includes("youtube") || selectedVideo.video_url.includes("youtu.be") ? (
                            <iframe
                              src={selectedVideo.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                              title={selectedVideo.title}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : selectedVideo.video_url.includes("vimeo") ? (
                            <iframe
                              src={selectedVideo.video_url.replace("vimeo.com/", "player.vimeo.com/video/")}
                              title={selectedVideo.title}
                              className="w-full h-full"
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <video
                              src={selectedVideo.video_url}
                              controls
                              className="w-full h-full"
                              poster={selectedVideo.thumbnail_url}
                            >
                              Browser-ul tău nu suportă video.
                            </video>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                          <div className="text-center">
                            <Play className="w-16 h-16 text-white/50 mx-auto mb-4" />
                            <p className="text-white/60">Video în pregătire</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Video Info */}
                    <div className="mt-6">
                      <h2 className="text-2xl font-bold text-slate-900 font-['Archivo']">
                        {selectedVideo.title}
                      </h2>
                      {selectedVideo.description && (
                        <p className="text-slate-600 mt-3 leading-relaxed">
                          {selectedVideo.description}
                        </p>
                      )}
                      {selectedVideo.duration && (
                        <div className="flex items-center gap-2 mt-4 text-slate-500">
                          <Clock className="w-4 h-4" />
                          <span>{selectedVideo.duration}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Lessons List - Sidebar */}
              <div className="lg:col-span-1">
                <h3 className="text-lg font-bold text-slate-900 mb-4 font-['Archivo']">
                  Lecții Disponibile
                </h3>
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-3"
                >
                  {freeLessons.map((lesson, index) => (
                    <motion.div key={lesson.id} variants={itemVariants}>
                      <Card 
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedVideo?.id === lesson.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                        onClick={() => setSelectedVideo(lesson)}
                      >
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            {/* Thumbnail */}
                            <div className="w-24 h-16 bg-slate-200 rounded-lg flex-shrink-0 overflow-hidden relative">
                              {lesson.thumbnail_url ? (
                                <img 
                                  src={lesson.thumbnail_url} 
                                  alt={lesson.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
                                  <Play className="w-6 h-6 text-white" />
                                </div>
                              )}
                              {selectedVideo?.id === lesson.id && (
                                <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                                    <Play className="w-4 h-4 text-blue-600 ml-0.5" />
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-slate-900 text-sm line-clamp-2">
                                {lesson.title}
                              </h4>
                              {lesson.duration && (
                                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {lesson.duration}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>

                {/* CTA to full course */}
                <div className="mt-8 p-6 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl text-white">
                  <Lock className="w-8 h-8 mb-3 opacity-80" />
                  <h4 className="font-bold text-lg mb-2">Vrei mai multe lecții?</h4>
                  <p className="text-blue-100 text-sm mb-4">
                    Accesează toate cursurile video cu un abonament.
                  </p>
                  <Link to="/register">
                    <Button className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold">
                      Înscrie-te Acum
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Archivo']">
              De Ce Să Înveți Cu Noi?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: BookOpen,
                title: "Explicații Logice",
                description: "Fiecare regulă explicată pas cu pas, cu exemple din viața reală"
              },
              {
                icon: Video,
                title: "Vizual și Clar",
                description: "Scheme și animații care te ajută să înțelegi intersecțiile"
              },
              {
                icon: CheckCircle,
                title: "Teste Identice DRPCIV",
                description: "Exersezi pe chestionare identice cu cele de la examen"
              }
            ].map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                    <p className="text-slate-600 text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold font-['Archivo']">
              Pregătit Să Începi?
            </h2>
            <p className="text-slate-400 mt-4">
              Creează un cont gratuit și accesează toate resursele pentru examenul auto.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/register">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 font-semibold">
                  Creează Cont Gratuit
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/programari">
                <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 font-semibold">
                  Programează-te la Școală
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
